import { useReactiveVar } from "@apollo/client";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import listOfApolloVar from "../../../apollo/apollo-var";
import {
  ApprovalEntity,
  ApprovalSearchType,
  Approval_For_List_Type,
  GetListOfApprovalQueryVariables,
  useCountListOfApprovalLazyQuery,
  useGetListOfApprovalLazyQuery
} from "../../../generated/graphql";
import useNewSortBy from "../../../hooks/use-new-sort-by/use-new-sort-by";
import usePageControl from "../../../hooks/use-page-control/use-page-control";
import routes from "../../../routes";
import EChannel from "../../../Utils/EChannel/e-channel";
import { SearchFieldValues } from "../../asonic-table/asonic-search";
import ApprovalDetailPopup, {
  ApprovalType,
  IApprovalDetailMessage
} from "../approval-detail-popup";
import ApprovalListForm from "./approval-list-form";
import { PopUpContainer } from "./approval-waiting-list";

const Container = styled.div`
  display: flex;
  flex: 1;
`;

const title = "나의 결재";

function ListOfMyApproval() {
  const isRequest = useRef<boolean>(false);
  const navigate = useNavigate();
  const { fieldSort, handleFieldSort } = useNewSortBy();
  const [selectedRow, setSelectedRow] = useState<ApprovalEntity | undefined>(
    undefined
  );
  const [showApprovalPopup, setShowApprovalPopup] = useState<boolean>(false);
  const [selectedRowIndex, setSelectedRowIndex] = useState<number>(0);

  const handleSelectedRowIndex = useCallback((index: number) => {
    setSelectedRowIndex(index);
  }, []);

  const handleShowApprovalPopup = useCallback((value: boolean) => {
    setShowApprovalPopup(value);
  }, []);
  const approvalData = useReactiveVar(listOfApolloVar.approvalDataVar);
  const approvalType = useReactiveVar(listOfApolloVar.approvalTypeVar);

  const handleSelectedRow = useCallback((row: ApprovalEntity) => {
    setSelectedRow(row);
  }, []);

  const [searchType, setSearchType] = useState<ApprovalSearchType>(
    ApprovalSearchType.DocumentNumber
  );

  const [searchValue, setSearchValue] = useState("");

  const { currentPage, handleCurrentPage, take, handleTake } = usePageControl();

  const handleSearch = useCallback(
    (data: SearchFieldValues) => {
      if (data.type) {
        const INIT_PAGE = 1;
        setSearchType(data.type as ApprovalSearchType);
        setSearchValue(data.value ?? "");
        handleCurrentPage(INIT_PAGE);
        setSearchValue(data.value as string);
      }
    },
    [handleCurrentPage]
  );

  const bc = useMemo(() => {
    if (selectedRow) {
      return new BroadcastChannel(EChannel.APPROVAL);
    }
    return;
  }, [selectedRow]);

  const bcMoveToPage = useMemo(
    () => new BroadcastChannel(EChannel.MOVE_TO_PAGE),
    []
  );

  const [getListOfMyApproval, { data, loading }] =
    useGetListOfApprovalLazyQuery({
      onError(error) {
        console.log(error);
      },
      fetchPolicy: "cache-and-network",
      onCompleted(data) {
        if (data.getListOfApproval.ok && data.getListOfApproval.list) {
          if (isRequest.current) {
            setSelectedRowIndex(data.getListOfApproval.list?.length - 1);
            isRequest.current = false;
          }
        }
      }
    });

  const [countListOfApproval, { data: countData, loading: countLoading }] =
    useCountListOfApprovalLazyQuery({
      fetchPolicy: "cache-and-network"
    });

  const list: ApprovalEntity[] = useMemo(() => {
    return data?.getListOfApproval.list || [];
  }, [data]);

  const total: number = useMemo(() => {
    return countData?.countListOfApproval.count ?? 0;
  }, [countData]);

  const totalPage = useMemo(() => Math.ceil(total / take), [total, take]);

  const handleSelectForm = useCallback(
    (isNext: boolean) => {
      if (isNext) {
        if (
          typeof selectedRowIndex === "number" &&
          selectedRowIndex < list.length - 1
        ) {
          const nextIndex = selectedRowIndex + 1;
          handleSelectedRowIndex(nextIndex);
        } else if (currentPage < totalPage) {
          handleSelectedRowIndex(0);
          handleCurrentPage(currentPage + 1);
        }
      } else {
        if (typeof selectedRowIndex === "number" && selectedRowIndex > 0) {
          const preIndex = selectedRowIndex - 1;
          handleSelectedRowIndex(preIndex);
        } else if (currentPage > 1) {
          handleCurrentPage(currentPage - 1);
          if (!isRequest.current) {
            isRequest.current = true;
          }
        }
      }
    },
    [selectedRowIndex, list, handleSelectedRowIndex, currentPage, total]
  );

  useEffect(() => {
    if (list.length > 0 && !isRequest.current) {
      const selectedRow = list[selectedRowIndex];
      listOfApolloVar.approvalDataVar(selectedRow);
    }
  }, [list, selectedRowIndex]);

  useEffect(() => {
    const payload: GetListOfApprovalQueryVariables = {
      approvalType: Approval_For_List_Type.My,
      searchType,
      searchValue,
      take,
      page: currentPage,
      fieldSort
    };
    getListOfMyApproval({
      variables: payload
    });
  }, [
    searchValue,
    searchType,
    getListOfMyApproval,
    take,
    currentPage,
    fieldSort
  ]);

  useEffect(() => {
    const payload = {
      approvalType: Approval_For_List_Type.My,
      searchType,
      searchValue
    };
    countListOfApproval({
      variables: payload
    });
  }, [searchValue, searchType, countListOfApproval]);

  useEffect(() => {
    if (bcMoveToPage) {
      bcMoveToPage.onmessage = event => {
        const message: IApprovalDetailMessage = event.data;
        if (message.type === ApprovalType.MOVE_TO_TEMPORARY_STORAGE_PAGE) {
          bcMoveToPage.close();
          navigate(routes.pageRoutes.approvalTemporaryDocument);
        }
      };
    }
    return () => {
      if (bcMoveToPage) {
        bcMoveToPage.close();
      }
    };
  }, [bcMoveToPage, navigate]);

  useEffect(() => {
    if (bc && selectedRow) {
      bc.onmessage = event => {
        const newMessage: IApprovalDetailMessage = {
          type: ApprovalType.MY_APPROVAL,
          data: selectedRow,
          isRequest: false
        };
        bc.postMessage(newMessage);
        setSelectedRow(undefined);
      };
    }
    return () => {
      if (bc) {
        bc.close();
      }
    };
  }, [bc, selectedRow]);

  return (
    <Container>
      {showApprovalPopup ? (
        <PopUpContainer>
          <ApprovalDetailPopup
            approvalData={approvalData}
            newApprovalType={approvalType}
            handleShowApprovalPopup={handleShowApprovalPopup}
            handleSelectForm={handleSelectForm}
          />
        </PopUpContainer>
      ) : (
        <ApprovalListForm
          list={list}
          title={title}
          fieldSort={fieldSort}
          handleFieldSort={handleFieldSort}
          // downloadExcel={downloadExcel}
          // handleSelectRow={handleSelectRow}
          isLoading={loading ?? countLoading}
          // selectedRow={selectedRow}
          handleSelectedRow={handleSelectedRow}
          handleShowApprovalPopup={handleShowApprovalPopup}
          currentPage={currentPage}
          handleCurrentPage={handleCurrentPage}
          take={take}
          handleTake={handleTake}
          handleSearch={handleSearch}
          handleSelectedRowIndex={handleSelectedRowIndex}
          total={total}
        />
      )}
    </Container>
  );
}

export default ListOfMyApproval;
